
import React, { useState } from 'react';
import { Order, Language, OrderStatus, UserRole } from '../types';
import { TRANSLATIONS } from '../translations';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, CheckCircle, Plus, X, Save } from 'lucide-react';

interface CampaignCalendarProps {
  orders: Order[];
  language: Language;
  userRole?: UserRole; // Optional to determine what name to show
}

interface CustomEvent {
    id: string;
    title: string;
    date: string; // YYYY-MM-DD
    type: 'custom';
}

export const CampaignCalendar: React.FC<CampaignCalendarProps> = ({ orders, language, userRole = UserRole.PROJECT }) => {
  const t = TRANSLATIONS[language];
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Custom Events State
  const [customEvents, setCustomEvents] = useState<CustomEvent[]>([]);
  const [addingEventDate, setAddingEventDate] = useState<string | null>(null);
  const [newEventTitle, setNewEventTitle] = useState('');

  // Helper to get days in month
  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Helper to get day of week for the 1st of the month (0 = Sunday)
  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month, 1).getDay();
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const startDayOffset = getFirstDayOfMonth(year, month);
  const today = new Date();
  const isCurrentMonth = today.getMonth() === month && today.getFullYear() === year;

  const handlePrevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const handleAddEventClick = (dateStr: string) => {
      setAddingEventDate(dateStr);
      setNewEventTitle('');
  };

  const handleSaveEvent = () => {
      if (addingEventDate && newEventTitle.trim()) {
          setCustomEvents([...customEvents, {
              id: `evt_${Date.now()}`,
              title: newEventTitle,
              date: addingEventDate,
              type: 'custom'
          }]);
          setAddingEventDate(null);
          setNewEventTitle('');
      }
  };

  const getEventsForDay = (day: number) => {
     // Format: YYYY-MM-DD
     const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
     
     // Filter orders
     const dayOrders = orders.filter(o => o.dueDate === dateStr).map(o => ({ ...o, type: 'order' }));
     // Filter custom events
     const dayCustom = customEvents.filter(e => e.date === dateStr);
     
     return [...dayOrders, ...dayCustom];
  };

  const getStatusColor = (status: OrderStatus) => {
      switch (status) {
          case OrderStatus.COMPLETED: return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30';
          case OrderStatus.IN_PROGRESS: return 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30';
          case OrderStatus.REVIEW_DATA: return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
          case OrderStatus.DISPUTED: return 'bg-rose-500/20 text-rose-300 border-rose-500/30';
          default: return 'bg-amber-500/20 text-amber-300 border-amber-500/30'; // Negotiating/Escrow
      }
  };

  // Formatting date for header
  const monthLabel = new Intl.DateTimeFormat(language === 'en' ? 'en-US' : 'zh-CN', { month: 'long', year: 'numeric' }).format(currentDate);

  const weekDays = language === 'en' 
    ? ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    : ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden shadow-lg h-full flex flex-col">
      <div className="p-4 border-b border-slate-700 flex justify-between items-center bg-slate-900/50">
         <h3 className="font-bold text-white flex items-center gap-2">
            <CalendarIcon className="text-indigo-400" />
            {t.campaignCalendar}
         </h3>
         <div className="flex items-center gap-4">
            <button onClick={handlePrevMonth} className="p-1.5 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors">
                <ChevronLeft size={18} />
            </button>
            <span className="text-sm font-bold text-white min-w-[100px] text-center">{monthLabel}</span>
            <button onClick={handleNextMonth} className="p-1.5 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors">
                <ChevronRight size={18} />
            </button>
         </div>
      </div>
      
      <div className="p-4 flex-1 overflow-y-auto">
         <div className="grid grid-cols-7 gap-2 mb-2">
            {weekDays.map(d => (
                <div key={d} className="text-center text-xs font-bold text-slate-500 uppercase py-1">{d}</div>
            ))}
         </div>
         <div className="grid grid-cols-7 gap-2">
             {/* Empty start slots */}
             {[...Array(startDayOffset)].map((_, i) => (
                 <div key={`empty-${i}`} className="h-24 md:h-28 bg-slate-900/20 rounded-lg border border-transparent"></div>
             ))}
             
             {/* Days */}
             {[...Array(daysInMonth)].map((_, i) => {
                 const day = i + 1;
                 const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                 const events = getEventsForDay(day);
                 const isToday = isCurrentMonth && day === today.getDate();
                 const isAdding = addingEventDate === dateStr;

                 return (
                     <div 
                        key={day} 
                        className={`h-24 md:h-28 rounded-lg p-2 flex flex-col transition-all group relative ${
                            isToday 
                                ? 'bg-indigo-900/20 border border-indigo-500/50 shadow-[0_0_10px_rgba(99,102,241,0.1)]' 
                                : 'bg-slate-900/50 border border-slate-700/50 hover:border-slate-600'
                        }`}
                     >
                        <div className="flex justify-between items-start mb-1">
                            <span className={`text-xs font-medium block w-6 h-6 leading-6 text-center rounded-full ${
                                isToday ? 'bg-indigo-600 text-white' : 'text-slate-500'
                            }`}>
                                {day}
                            </span>
                            
                            {/* Add Event Button (Visible on Hover) */}
                            {!isAdding && (
                                <button 
                                    onClick={(e) => { e.stopPropagation(); handleAddEventClick(dateStr); }}
                                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white transition-opacity"
                                    title={t.addEvent}
                                >
                                    <Plus size={12} />
                                </button>
                            )}
                        </div>
                        
                        <div className="flex-1 overflow-y-auto space-y-1.5 scrollbar-none">
                            {/* Input Mode */}
                            {isAdding && (
                                <div className="bg-slate-800 p-1.5 rounded border border-indigo-500 z-10 relative">
                                    <input 
                                        autoFocus
                                        type="text" 
                                        value={newEventTitle}
                                        onChange={(e) => setNewEventTitle(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleSaveEvent()}
                                        placeholder="Event..."
                                        className="w-full bg-transparent text-[10px] text-white focus:outline-none mb-1"
                                    />
                                    <div className="flex justify-end gap-1">
                                        <button onClick={() => setAddingEventDate(null)} className="text-rose-400 hover:text-rose-300"><X size={10} /></button>
                                        <button onClick={handleSaveEvent} className="text-emerald-400 hover:text-emerald-300"><Save size={10} /></button>
                                    </div>
                                </div>
                            )}

                            {events.map((ev: any) => (
                                <div 
                                    key={ev.id} 
                                    className={`text-[10px] border rounded px-1.5 py-1 truncate cursor-pointer transition-transform hover:scale-[1.02] ${
                                        ev.type === 'custom' 
                                            ? 'bg-slate-700 text-slate-300 border-slate-600' 
                                            : getStatusColor(ev.status)
                                    }`}
                                    title={ev.type === 'custom' ? ev.title : `${ev.serviceTitle} - ${userRole === UserRole.PROJECT ? ev.kolName : ev.projectName}`}
                                >
                                    {ev.type === 'custom' ? (
                                        <span className="font-medium">{ev.title}</span>
                                    ) : (
                                        <>
                                            <div className="font-bold flex items-center gap-1">
                                                {ev.status === OrderStatus.COMPLETED && <CheckCircle size={8} />}
                                                {userRole === UserRole.PROJECT ? ev.kolName : ev.projectName}
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                     </div>
                 );
             })}
         </div>
      </div>
    </div>
  );
};
